import React, { useCallback, useEffect, useState } from "react";
import TabContainerHOC from "../../hocs/TabContainerHOC";
import Notification from "../../../global/Notification";
import StickySaveBar from "../../../global/StickySaveBar";
import { Store } from "../../../../types";
import { getStoreById } from "../../../../lib/admin";
import { getCustomizationSection, saveCustomizationSection } from "../../../../lib/customization/customizationService";
import { PageProps, SliderItem } from "../../../../types";
import { useLocation, useNavigate } from "react-router-dom";
import { EditIcon } from "../../../../assets/EditIcon";
import { DragIcon } from "../../../../assets/DragIcon";
import { RemoveIcon } from "../../../../assets/RemoveIcon";
import { PlusIconGuide } from "../../../../assets/PlusIconGuide";
import { CarouselSlideVideoCard } from "./CarouselVideoSlideCard";
import { CarouselImageSlideCard } from "./CarouselImageSlideCard";
import { CarouselLivePreview } from "./CarouselLivePreview";
import ConfirmModal from "../../../global/ConfirmModal";
import { ensureIds } from "../hooks-functions/CarouselUtils";
import { useUnsavedChanges, useUnsavedChangesGuard } from "../../hooks/useUnsavedChangesGuard";

export default function CarouselCustomizationPage({ model }: PageProps) {
    const [store, setStore] = useState<undefined | Store>(undefined);
    const [interval, setInterval] = useState<number>();
    const [items, setItems] = useState<SliderItem[]>([]);
    const [originalData, setOriginalData] = useState<{
        interval: number;
        items: SliderItem[];
    } | null>(null);
    const [calling, setCalling] = useState<boolean>(true);
    const [hasChanges, setHasChanges] = useState<boolean>(false);
    const [loadFailed, setLoadFailed] = useState(false);
    const [notification, setNotification] = useState<{ message: string | null; type: "success" | "error" }>({
        message: null,
        type: "success",
    });
    const [saveError, setSaveError] = useState<string | null>(null);
    const [dragIndex, setDragIndex] = useState<number | null>(null);
    const [bodyOverflow, setBodyOverflow] = useState<string | null>(null);
    const [slideToDelete, setSlideToDelete] = useState<number | null>(null);

    const navigate = useNavigate();
    const location = useLocation();

    const { runWithConfirmation } = useUnsavedChanges();

    const closeNotification = useCallback(() => setNotification({ message: null, type: "success" }), []);
    const dismissSaveError = useCallback(() => setSaveError(null), []);

    useEffect(() => {
        const successMessage = (location.state as { successMessage?: string } | null)?.successMessage;
        if (!successMessage) return;
        setNotification({ message: successMessage, type: "success" });
        navigate({ pathname: location.pathname, search: location.search }, { replace: true, state: null });
    }, []);

    const onDragStartCard = (idx: number, e: React.DragEvent) => {
        setDragIndex(idx);
        e.dataTransfer.effectAllowed = "move";
        e.dataTransfer.setData("text/plain", String(idx));
        const card = (e.currentTarget as HTMLElement).closest(".slideCard") as HTMLElement | null;
        if (card) {
            const rect = card.getBoundingClientRect();
            e.dataTransfer.setDragImage(card, e.clientX - rect.left, e.clientY - rect.top);
        }
    };

    const onDragOverCard = (e: React.DragEvent) => {
        e.preventDefault();
        e.dataTransfer.dropEffect = "move";
    };

    const onDropOnCard = (idx: number, e: React.DragEvent) => {
        e.preventDefault();
        const from = dragIndex ?? parseInt(e.dataTransfer.getData("text/plain"), 10);
        if (isNaN(from) || from === idx) {
            setDragIndex(null);
            return;
        }
        setItems(prev => {
            const next = [...prev];
            const tmp = next[from];
            next[from] = next[idx];
            next[idx] = tmp;
            return next;
        });
        setHasChanges(true);
        setDragIndex(null);
    };

    const onDragEndCard = () => setDragIndex(null);

    const onTouchStartCard = (idx: number) => {
        if (typeof document !== "undefined") {
            if (bodyOverflow === null) {
                setBodyOverflow(document.body.style.overflow || "");
            }
            document.body.style.overflow = "hidden";
        }
        setDragIndex(idx);
    };

    const onTouchMoveCard = (e: React.TouchEvent) => {
        if (dragIndex === null) return;
        const touch = e.touches[0];
        const element = document.elementFromPoint(touch.clientX, touch.clientY);
        const card = element?.closest(".slideCard") as HTMLElement | null;
        if (!card) return;
        const overIndex = parseInt(card.getAttribute("data-index") || "-1", 10);
        if (overIndex < 0 || overIndex === dragIndex) {
            e.preventDefault();
            return;
        }
        setItems(prev => {
            const next = [...prev];
            const tmp = next[dragIndex];
            next[dragIndex] = next[overIndex];
            next[overIndex] = tmp;
            return next;
        });
        setDragIndex(overIndex);
        setHasChanges(true);
        e.preventDefault();
    };

    const onTouchEndCard = () => {
        if (typeof document !== "undefined" && bodyOverflow !== null) {
            document.body.style.overflow = bodyOverflow;
        }
        setDragIndex(null);
    };

    const openCreateModal = () => {
        const target = `/management/${model.storeId}/customization/carousel/new`;
        runWithConfirmation(() => {
            navigate(target);
        });
    };

    const openEditorFor = (id: string) => {
        const target = `/management/${model.storeId}/customization/carousel/${id}`;
        runWithConfirmation(() => {
            navigate(target);
        });
    };


    const loadStore = async () => {
        try {
            const storeData = await getStoreById(parseInt(model.storeId));
            setStore(storeData);
        } catch (error) {
            setStore(undefined);
        }
    };

    useEffect(() => {
        setItems([]);
        loadStore();
        getCustomizationSection<any>(model.storeId, 'HomepageCarousel')
            .then((data) => {
                const { interval, items } = data;
                const itemsWithIds = ensureIds(items);

                const dbOriginal = {
                    interval: interval || 0,
                    items: itemsWithIds,
                };
                setOriginalData(dbOriginal);

                setItems(dbOriginal.items);
                setInterval(dbOriginal.interval);
                setHasChanges(false);
            })
            .catch(() => {
                setLoadFailed(true);
                setNotification({ message: "Failed to load data. Please try again or contact support.", type: "error" });
            })
            .finally(() => {
                setCalling(false);
            });
    }, [model.storeId]);

    const showNotification = (message: string, type: "success" | "error") => {
        setNotification({ message, type });
    };

    const handleCancel = () => {
        if (originalData) {
            setInterval(originalData.interval);
            setItems(originalData.items);
            setHasChanges(false);
        }
        setSaveError(null);
    };

    const handleConfirmDelete = async () => {
        if (slideToDelete === null || !originalData) return;

        const itemToDelete = items[slideToDelete];
        if (!itemToDelete?.id) {
            showNotification("Could not identify the slide to delete.", "error");
            return;
        }
        const idToDelete = itemToDelete.id;

        const newItems = items.filter(it => it.id !== idToDelete);
        const newOriginalItems = originalData.items.filter(it => it.id !== idToDelete);

        const body = {
            interval: originalData.interval,
            items: newOriginalItems,
        };

        setCalling(true);
        try {
            await saveCustomizationSection({
                storeId: model.storeId,
                metafield: 'HomepageCarousel',
                section: 'carousel',
                payload: body,
                lastValue: originalData,
                action: 'delete',
            });

            const newOriginalData = {
                ...originalData,
                items: newOriginalItems,
            };

            setItems(newItems);
            setOriginalData(newOriginalData);

            const hasPending =
                interval !== newOriginalData.interval ||
                newItems.length !== newOriginalItems.length ||
                newItems.some((it, i) => it.id !== newOriginalItems[i]?.id);

            setHasChanges(hasPending);

            showNotification("Slide removed successfully.", "success");

        } catch (error) {
            showNotification("Failed to remove slide. Please try again.", "error");
        } finally {
            setCalling(false);
            setSlideToDelete(null);
        }
    };

    const send = async (): Promise<boolean> => {
        if (!hasChanges) return false;
        const missingImage = items.some(item => {
            const isVideo = !!(item.videoUrl && item.videoUrl.trim());
            return !isVideo && item.src === "";
        });
        if (missingImage) {
            setSaveError("Please upload an image for each slide.");
            return false;
        }
        setCalling(true);
        setSaveError(null);
        const body = {
            interval,
            items,
        };
        try {
            await saveCustomizationSection({
                storeId: model.storeId,
                metafield: 'HomepageCarousel',
                section: 'carousel',
                payload: body,
                lastValue: originalData,
            });
            showNotification("Update successful.", "success");
            setOriginalData({ interval: interval!, items });
            setHasChanges(false);
            return true;
        } catch (error) {
            setSaveError("Failed to save settings. Please try again.");
            return false;
        } finally {
            setCalling(false);
        }
    };

    const unsavedChangesModal = useUnsavedChangesGuard({ hasChanges, save: send });

    return (
        <>
            <TabContainerHOC title="Carousel" containerClass="carousel" loading={calling}>
                {!loadFailed && (
                    <>
                        <div className="slider-main">
                            <div style={{ width: "100%" }}>
                                <div className="slider-first-container">
                                    <p className="slider-subtitle"><b>Drag slides</b> to reorder.</p>
                                    <button onClick={openCreateModal}>
                                        + Add Slide
                                    </button>
                                </div>
                                <hr className="slideCard-separator" />
                                {items.map((it, i) => {
                                    const isVideo = !!(it.videoUrl && it.videoUrl.trim());
                                    const sharedProps = {
                                        key: it.id || i,
                                        index: i,
                                        isDragging: dragIndex === i,
                                        onEdit: () => openEditorFor(it.id!),
                                        onRemove: () => setSlideToDelete(i),
                                        onDragStart: onDragStartCard,
                                        onDragOver: onDragOverCard,
                                        onDrop: onDropOnCard,
                                        onDragEnd: onDragEndCard,
                                        onTouchStart: onTouchStartCard,
                                        onTouchMove: onTouchMoveCard,
                                        onTouchEnd: onTouchEndCard,
                                    };

                                    return isVideo ? (
                                        <CarouselSlideVideoCard
                                            {...sharedProps}
                                            videoUrl={it.videoUrl!}
                                        />
                                    ) : (
                                        <CarouselImageSlideCard
                                            {...sharedProps}
                                            title={it.h2.text}
                                            desktopSrc={it.src}
                                            mobileSrc={it.mobile}
                                        />
                                    );
                                })}
                            </div>
                            <div className="col carousel__addSlide">
                                <h3>Actions Guide</h3>
                                <ul>
                                    <li><PlusIconGuide /> Add a new slide</li>
                                    <li><EditIcon /> Edit slide</li>
                                    <li><DragIcon /> Drag slides to reorder</li>
                                    <li><RemoveIcon /> Remove</li>
                                </ul>
                            </div>
                        </div>

                        {items.length > 0 && (
                            <CarouselLivePreview items={items} />
                        )}
                    </>
                )}
            </TabContainerHOC>
            <StickySaveBar
                visible={hasChanges}
                saving={calling}
                onSave={send}
                onCancel={handleCancel}
                alignWithSelector=".leftVertical"
                errorMessage={saveError ?? undefined}
                onErrorDismiss={dismissSaveError}
            />
            <Notification message={notification.message} type={notification.type} onClose={closeNotification} />

            <ConfirmModal
                open={slideToDelete !== null}
                title="Remove slide?"
                message= "Are you sure you want to remove this slide? This action cannot be undone."
                onCancel={() => setSlideToDelete(null)}
                onConfirm={handleConfirmDelete}
            />

            {unsavedChangesModal}
        </>
    );
}